import {inject, injectable} from "inversify";
import {EventEmitter, EventSubscriber, HasEventName} from "@/modules/base/events";
import {ConfigRepository} from "@/modules/base/services/ConfigRepository";
import {AfterDayEvaluationEvent} from "@/modules/season/events/AfterDayEvaluationEvent";
import {TransferListing, TransferOffer} from "../entity";
import chance from "../../base/services/chance";
import {Player, Team} from "../../team/entity";
import OfferCreator from "../services/OfferCreator";
import {TransferOfferStatusEnum} from "@/modules/transfer-market/enums";
import MarketValueCalculator from "@/modules/team/services/MarketValueCalculator";
import {WeekdayEnum} from "@/modules/base/enums";
import DirectTransferHandler from "@/modules/transfer-market/services/DirectTransferHandler";
import {AfterSeasonEvaluationEvent} from "@/modules/season/events/AfterSeasonEvaluationEvent";
import FastPlayerFactory from "@/modules/team/services/factories/FastPlayerFactory";
import AIServiceProvider from "@/modules/ai/services/AIServiceProvider";

@injectable()
export default class AITransferEventSubscriber implements EventSubscriber {
  constructor(
    @inject(EventEmitter) private em: EventEmitter,
    @inject(ConfigRepository) private configRepository: ConfigRepository,
    @inject(OfferCreator) private offerCreator: OfferCreator,
    @inject(MarketValueCalculator) private marketValueCalculator: MarketValueCalculator,
    @inject(DirectTransferHandler) private directTransferHandler: DirectTransferHandler,
    @inject(FastPlayerFactory) private fastPlayerFactory: FastPlayerFactory,
    @inject(AIServiceProvider) private aiServiceProvider: AIServiceProvider,
  ) {
  }

  priority(): number {
    return 0;
  }

  init(): { event: HasEventName; callback: (event: any) => void }[] {
    return [
      {event: AfterSeasonEvaluationEvent, callback: this.onAfterSeasonEvaluationEvent},
      {event: AfterDayEvaluationEvent, callback: this.createOfferForListing},
      {event: AfterDayEvaluationEvent, callback: this.createRandomOffer},
      {event: AfterDayEvaluationEvent, callback: this.transferRandomPlayer},
    ];
  }

  public async onAfterSeasonEvaluationEvent(event: AfterSeasonEvaluationEvent) {
    await (
      await Team
        .createQueryBuilder('team')
        .leftJoinAndSelect('team.players', 'players')
        .innerJoinAndSelect('team.country', 'country')
        .innerJoinAndSelect('country.region', 'region')
        .where('team.user = false')
        .getMany()
    )
      .filter((team: Team) => team.players.length < this.configRepository.get('teams.minPlayerCount')(team))
      .forEachAsync(async (team: Team) => {
        await times(this.configRepository.get('teams.minPlayerCount')(team) - team.players.length)
          .forEachAsync(async () => {
            const player = await this.fastPlayerFactory.generateYoungFiller(team, {
              callback: async player => {
                player.position = await this.aiServiceProvider.getTransferDecider(team.aiQualityLevel).getPosition(team);
                player.age = chance.integer({
                  min: 16,
                  max: 21
                });
              }
            });
          });
      });

    await this.fastPlayerFactory.flush();
  }

  public async transferRandomPlayer(event: AfterDayEvaluationEvent) {
    if (event.season.currentDate.date() === WeekdayEnum.SUNDAY) {
      return;
    }

    const teamCount = await Team
      .createQueryBuilder('team')
      .getCount();

    const teams = await Team
      .createQueryBuilder('team')
      .innerJoinAndSelect('team.players', 'player')
      .where('team.user != true')
      .orderBy('RANDOM()')
      .limit(this.configRepository.get('transferMarket.random.transfersPerWeek')(teamCount))
      .getMany();

    await teams.forEachAsync(async (team: Team) => {
      await this.directTransferHandler.handle(team);
    });
  }

  public async createRandomOffer(event: AfterDayEvaluationEvent) {
    if (!chance.bool({likelihood: this.configRepository.get('transferMarket.random.percentChanceForOffer')})) {
      return;
    }

    const player = await Player
      .createQueryBuilder('player')
      .innerJoinAndSelect('player.team', 'team')
      .where('team.user = true AND player.endsCareer = false')
      .orderBy('RANDOM()')
      .getOne();

    if (!player) {
      return;
    }

    const query = Team
      .createQueryBuilder('team')
      .where(`team.user = false`)
      .orderBy('RANDOM()');

    const offers = await TransferOffer
      .createQueryBuilder('offer')
      .innerJoinAndSelect('offer.player', 'player')
      .where('offer.status = :statusOpened', {statusOpened: TransferOfferStatusEnum.OPENED})
      .andWhere('player.id = :playerId', {playerId: player.id})
      .getMany();

    if (offers.length > 0) {
      query.andWhere(`team.id NOT IN (${offers.map(o => o.teamId).join(',')})`);
    }

    const team = await query.getOne();
    const marketValue = await this.marketValueCalculator.calculate(player);

    if (team) {
      const price = Math.round(chance.integer(this.configRepository.get('transferMarket.random.aiOfferPriceRange')) / 100 * marketValue);

      await this.offerCreator.create(player, team, price);
    }
  }

  public async createOfferForListing(event: AfterDayEvaluationEvent) {
    const listings = await TransferListing
      .createQueryBuilder('listing')
      .innerJoinAndSelect('listing.player', 'player')
      .innerJoinAndSelect('player.team', 'team')
      .leftJoinAndSelect('listing.offers', 'offer')
      .where('team.user = true')
      .getMany();

    await listings.forEachAsync(async (listing: TransferListing) => {
      if (
        chance.bool({likelihood: this.configRepository.get('transferMarket.listings.percentChanceForOffer')})
        && listing.offers.length < this.configRepository.get('transferMarket.listings.offerLimitPerListing')
      ) {
        const query = Team
          .createQueryBuilder('team')
          .where(`team.user = false AND team.id NOT IN (${listing.offers.map(o => o.teamId).join(',')})`)
          .orderBy('RANDOM()');

        const team = await query.getOne();

        if (team) {
          const price = Math.round(chance.integer(this.configRepository.get('transferMarket.listings.aiOfferPriceRange')) / 100 * listing.price);

          await this.offerCreator.create(listing.player, team, price, listing);
        }
      }
    });
  }
}
